// @flow

import * as React from 'react';
import { Query } from 'react-apollo';
import { bind } from '@ta/common.api';
import classNames from '@ta/common.classnames';
import { ErrorBoundary } from '@ta/common.error-handling';
import { featureIsEnabled } from '@ta/common.features';
import Logging from '@ta/common.logging';
import { Sticky } from '@ta/common.position';
import DmoDwellTracking from '@ta/homepage.dmo-dwell-tracking';
import { RouteConsumer, buildUrl, parseUrl } from '@ta/common.routing';
import {
  ProfileSectionNarrow,
  SocialUgcActionSection,
  GeoOverviewSection,
} from '@ta/social.sections';
import { WithAuthzInfo } from '@ta/common.authz';
import { LoadingErrorBanner, RenderErrorBanner } from '@ta/loading.error';
import { ScrollFeedTooltip } from '@ta/social.educational-tooltips';
import { ExploreDestinationData } from '@ta/social.explore-destination';
import { SponsoredHomeQuery, TermsOfUseQuery, CanBoostQuery } from '@ta/social.feed';
import { InsightProfileExplicitPreferencesTripSetupQuery } from '@ta/social.offers';
import { TrackingNamespaceContext, HomeTrackingContext } from '@ta/tracking.interactions';
import RightHandRail from '@ta/social.right-hand-rail';
import { ExplicitPreferencesCta } from '@ta/onboarding.explicit-preferences-entry';
import type { ViewportCategory } from '@ta/common.state';
import AdTarget from '@ta/cpm.ad-target';
import AdWrapper from '@ta/cpm.home-page-ad-wrapper';
import HomeMixerFeed from './HomeMixerFeed';
import JvAdWrapper from './JvAdWrapper';

// eslint-disable-next-line css-modules/no-unused-class
import styles from './Home.less';

type ContainerProps = {
  renderErrorBanner: () => void,
  placement: string,
  puid: string,
  locationId: number,
  loggedInUserId: string,
  viewportCategory: ViewportCategory,
  newContentSubmittedTimestamp?: Date,
  latitude?: number,
  longitude?: number,
  /* callback when user uploads photo/video or post a link. this will trigger a feed refetch.  */
  triggerFeedRefresh: () => void,
  allowedTypes: ?Array<string>,
  tab: string,
  changeTab: string => void,
  changeTabCallback: string => void,
  route: Object,
  feedIdToRestore: ?string,
  isNearbyScope: boolean,
  currencyCode: string,
  locale: string,
  sessionId: string,
  uniqueId: string,
  ipGeo: number,
  adData: {
    image: string,
    url: string,
  },
};

type PlatformEnumInput = 'DESKTOP' | 'MOBILE_WEB';

const homeTrackingNamespace = 'home';

const isHomepageAdsRightRail = featureIsEnabled('homepage_ads_right_rail');

function getPlatformFromViewportCategory(viewportCategory: ViewportCategory): PlatformEnumInput {
  switch (viewportCategory.toLowerCase()) {
    case 'mobile':
      return 'MOBILE_WEB';
    default:
      return 'DESKTOP';
  }
}

const Home = (props: ContainerProps) => {
  // TRVX-13345 : Device mismatches shouldn't be triggering query refetches.
  const { current: initialViewportCategory } = React.useRef(props.viewportCategory);

  // Sponsorship variables
  const { locationId, sessionId, uniqueId, puid, adData } = props;
  const date = new Date(Date.now()).toISOString().slice(0, 10);
  const platform = getPlatformFromViewportCategory(initialViewportCategory);
  const geoId = parseInt(locationId, 10) || 1;
  const tracking = { sessionId, uniqueId, puid };

  const isDesktopLayout = props.viewportCategory === 'DESKTOP';

  return (
    <React.Fragment>
      <Query query={CanBoostQuery} skip={!props.loggedInUserId}>
        {() => null}
      </Query>
      <Query query={TermsOfUseQuery} skip={!props.loggedInUserId}>
        {() => null}
      </Query>
      <Query query={InsightProfileExplicitPreferencesTripSetupQuery}>{() => null}</Query>
      <LoadingErrorBanner />
      <div className={`social_home_feed ui_columns ${styles.home}`}>
        {isDesktopLayout && (
          <div className="ui_column is-3">
            <Sticky
              containerClassName={styles.fullHeight}
              offsetTop={118 /* To dodge two-row nav links */}
              offsetBottom={650 /* Sticky has issues unsticking at footer due to dynamic ad size */}
              stickyElement={
                <div>
                  <JvAdWrapper data={adData} />
                  <AdWrapper />
                </div>
              }
              append
            >
              <ProfileSectionNarrow
                isSingleObject
                userId={props.loggedInUserId}
                onError={props.renderErrorBanner}
              />
              <WithAuthzInfo onError={props.renderErrorBanner}>
                {(authzInfo, loading) => (
                  <SocialUgcActionSection
                    permissions={authzInfo}
                    permissionsLoading={loading}
                    fullWidth={false}
                    triggerFeedRefresh={props.triggerFeedRefresh}
                  />
                )}
              </WithAuthzInfo>
            </Sticky>
          </div>
        )}

        <div
          className={classNames('ui_column', {
            'is-12': !isDesktopLayout,
            'is-9': isDesktopLayout,
          })}
        >
          <div className={classNames({ ui_columns: isDesktopLayout })}>
            <div className={classNames({ 'ui_column is-8': isDesktopLayout })}>
              <HomeMixerFeed
                placement={props.placement}
                puid={props.puid}
                locationId={props.locationId}
                loggedInUserId={props.loggedInUserId}
                latitude={props.latitude}
                longitude={props.longitude}
                newContentSubmittedTimestamp={props.newContentSubmittedTimestamp}
                allowedTypes={props.allowedTypes}
                viewportCategory={initialViewportCategory}
                tab={props.tab}
                changeTabCallback={props.changeTabCallback}
                feedIdToRestore={props.feedIdToRestore}
                isNearbyScope={props.isNearbyScope}
                currencyCode={props.currencyCode}
                queryPlacement={props.isNearbyScope ? 'NEARBY_HOME' : 'HOME'}
                ipGeo={props.ipGeo}
                renderBeforeFeed={refetch => (
                  /* ML-4278 */
                  <ExplicitPreferencesCta onRefresh={refetch} />
                )}
              />
            </div>

            {isDesktopLayout && (
              <div
                className={classNames('ui_column is-4 section-top', {
                  [styles.fixed300]: isHomepageAdsRightRail,
                })}
              >
                {/* TRVX-13869 */}
                <RightHandRail />

                {isHomepageAdsRightRail && (
                  <ErrorBoundary>
                    <Query
                      query={SponsoredHomeQuery}
                      variables={{
                        geo: geoId,
                        locale: props.locale,
                        servlet: 'HOME',
                        date,
                        platform,
                        tracking,
                      }}
                      errorPolicy="all"
                    >
                      {({ data = {}, error }) => {
                        if (error) {
                          Logging.error(`Error fetching SponsoredHomeQuery `, error);
                        }

                        if (data && data.sponsoredHome) {
                          return null;
                        }

                        return <AdTarget size="300x250" minWinSize="1220" position="b" />;
                      }}
                    </Query>
                  </ErrorBoundary>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {featureIsEnabled('dmo_home_dwell_tracking') && (
        <DmoDwellTracking geoId={locationId} locale={props.locale} platform={platform} />
      )}
    </React.Fragment>
  );
};

Home.defaultProps = {
  latitude: undefined,
  longitude: undefined,
  newContentSubmittedTimestamp: undefined,
};

type ContainerState = {
  reset: boolean,
  newContentSubmittedTimestamp?: Date,
  tab: string,
};

class HomeContainer extends React.Component<ContainerProps, ContainerState> {
  static defaultProps = {
    latitude: undefined,
    longitude: undefined,
    newContentSubmittedTimestamp: undefined,
  };

  constructor(props: ContainerProps) {
    super(props);

    this.state = {
      reset: true,
      newContentSubmittedTimestamp: undefined,
      tab: props.tab,
    };
  }

  componentDidMount() {
    if (this.state.tab !== 'home') {
      this.updateUrl(this.state.tab);
    }
  }

  componentDidUpdate(prevProps: ContainerProps, prevState: ContainerState) {
    if (!prevState.reset && this.state.reset) {
      // eslint-disable-next-line no-restricted-globals
      window.scrollTo(0, 0);
    }
  }

  tabChanged = (tab: string) => {
    if (this.state.tab !== tab) {
      this.setState({ tab }, () => {
        this.updateUrl(tab);
      });
    }
  };

  // when a new photo/video is uploaded we update the newContentSubmittedTimestamp so that HomeMixerFeed is notified to refetch
  triggerFeedRefresh = () => {
    this.setState({ newContentSubmittedTimestamp: new Date() });
  };

  updateUrl = (tab: string) => {
    // eslint-disable-next-line no-restricted-globals
    const pathname = window.location.pathname === '/' ? '' : window.location.pathname;
    // eslint-disable-next-line no-restricted-globals
    const parsed = parseUrl(`${pathname}/${window.location.search}`);
    const newRoute = parsed || this.props.route;

    // default is home, no need to have any tab parameter
    if (tab === 'home') {
      delete newRoute.tab;
    } else {
      newRoute.tab = tab;
    }

    const newURL = buildUrl(newRoute);

    // Make sure we are not accidentally switching pathnames!
    // eslint-disable-next-line no-restricted-globals
    if (newURL && window.location.pathname && newURL.indexOf(window.location.pathname) === -1) {
      return;
    }

    // eslint-disable-next-line no-restricted-globals
    window.history.replaceState(newRoute, '', newURL);
  };

  topOfFeedRef: React.ElementRef<*> = React.createRef();

  render() {
    const homeProps = { ...this.props };
    homeProps.tab = this.state.tab;

    return (
      <TrackingNamespaceContext.Provider value={homeTrackingNamespace}>
        <HomeTrackingContext.Provider
          value={{ uid: this.props.puid, scopedGeoId: this.props.locationId }}
        >
          <div>
            <RenderErrorBanner>
              {renderErrorBanner => (
                <div ref={this.topOfFeedRef}>
                  <Home
                    {...homeProps}
                    tab={this.state.tab}
                    changeTabCallback={this.tabChanged}
                    renderErrorBanner={renderErrorBanner}
                    newContentSubmittedTimestamp={this.state.newContentSubmittedTimestamp}
                    triggerFeedRefresh={this.triggerFeedRefresh}
                  />
                </div>
              )}
            </RenderErrorBanner>
            <ScrollFeedTooltip elementToScrollTo={this.topOfFeedRef} />
          </div>
        </HomeTrackingContext.Provider>
      </TrackingNamespaceContext.Provider>
    );
  }
}

const HomeWithParams = (props: ContainerProps) => (
  <RouteConsumer>
    {({ route }) => (
      <HomeContainer
        {...props}
        tab="home"
        locationId={route.geo || props.locationId}
        allowedTypes={route.allowedTypes && route.allowedTypes.split(',')}
      />
    )}
  </RouteConsumer>
);
HomeWithParams.defaultProps = {
  latitude: undefined,
  longitude: undefined,
  newContentSubmittedTimestamp: undefined,
};

export default bind<ContainerProps, {||}, _, _, _, _>((state, stage) => {
  ExploreDestinationData.stage(state, stage);
  GeoOverviewSection.stage(state, stage);

  const loc = stage(`/data/1.0/daodao/sponsorship/${state.page.geoId}/ad`);

  return () => ({
    placement: 'HOME',
    locationId: state.page.geoId,
    loggedInUserId: state.auth.loggedInUserId,
    viewportCategory: state.meta.device.viewportCategory,
    puid: state.tracking.uid || '',
    latitude: parseFloat(state.route.latitude) || undefined,
    longitude: parseFloat(state.route.longitude) || undefined,
    feedIdToRestore: state.route.feedIdToRestore,
    route: state.route,
    isNearbyScope: state.route.isNearby,
    currencyCode: state.i18n.currencyCode,
    locale: state.i18n.locale,
    sessionId: state.tracking.sessionId || '',
    uniqueId: state.tracking.uniqueId || '',
    ipGeo: state.page.ipGeo,
    adData: loc.get().data || {},
  });
})(HomeWithParams);
